import { riderScreens } from "./navigation.ts";

/**
 * Which screen the address bar is asking for, and which journey it is about.
 *
 * A query parameter rather than a path, for the reason the dispatch console arrived at: a path needs
 * the server to answer every path with the application's document, and these channels are served as
 * static files from a gateway nobody here configures. `/?page=quote` is one document at one path and
 * needs nothing.
 *
 * It matters more here than it does on a console, for three reasons that have nothing to do with
 * bookmarks. Signing in leaves this origin for Keycloak and comes back, so the open screen has to be
 * written somewhere that survives the round trip. The acceptance check has to be able to put this
 * application on one named screen and compare it against one named design state — which is the whole
 * of `CLAUDE.md`'s step 1, "put it in the same state". And on this channel the address carries the
 * **identifiers the screens are about**, because EXP-002's journey is addressed by a ride request
 * and optionally a trip, and neither is discoverable from the other.
 */

export const PAGE_PARAMETER = "page";

/**
 * Where a rider lands with nothing asked for.
 *
 * The first screen of the flow. Every ride starts there, and it is the one screen of the thirty-two
 * that needs no identifier to be drawn.
 */
export const DEFAULT_PAGE_ID = "where-to";

/** Every identifier either built or designed, so a designed-but-unbuilt address is recognised. */
const DESIGNED: ReadonlySet<string> = new Set(riderScreens.map((screen) => screen.id));

/**
 * Read the screen out of a URL.
 *
 * An unrecognised identifier falls back to the default, which is the stale-bookmark case. A
 * recognised one that is not built is returned as it stands, so the router can say what that screen
 * needs — the two are different facts, and a rider told the wrong one would conclude the screen does
 * not exist.
 */
export function pageIdIn(url: string): string {
  const asked = new URL(url).searchParams.get(PAGE_PARAMETER);
  if (asked === null) return DEFAULT_PAGE_ID;
  return DESIGNED.has(asked) ? asked : DEFAULT_PAGE_ID;
}

/**
 * The same URL, asking for a different screen.
 *
 * Everything else in the address is preserved, which matters twice here: this is the URL the sign-in
 * flow stores as its return address, and it is also where the ride request and trip identifiers
 * live. Dropping a parameter would silently drop the journey the next screen is about.
 */
export function urlForPage(url: string, pageId: string): string {
  const next = new URL(url);
  if (pageId === DEFAULT_PAGE_ID) next.searchParams.delete(PAGE_PARAMETER);
  else next.searchParams.set(PAGE_PARAMETER, pageId);
  return next.toString();
}

/**
 * The same URL, asking for a different screen and carrying different identifiers.
 *
 * `undefined` removes a parameter, which is how `Get a new price` drops a dead `quote` on its way
 * back to `2 Choose`. Every other parameter is kept.
 */
export function urlFor(
  url: string,
  pageId: string,
  parameters: Readonly<Record<string, string | undefined>>,
): string {
  const next = new URL(urlForPage(url, pageId));
  for (const [key, value] of Object.entries(parameters)) {
    if (value === undefined) next.searchParams.delete(key);
    else next.searchParams.set(key, value);
  }
  return next.toString();
}

const UUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * An identifier named in the address, as a uuid or not at all.
 *
 * Every identifier EXP-002's and MOB-02's paths accept is `format: uuid` and both refuse anything
 * else with a 400, so a malformed value in a shared link would produce a refusal on every screen
 * with no clue as to why. Absent at least lets the screen say which fact is missing — and on this
 * application "which fact is missing" is most of what there is to say.
 */
export function identifierIn(url: string, parameter: string): string | undefined {
  const value = new URL(url).searchParams.get(parameter);
  return value !== null && UUID.test(value) ? value : undefined;
}

/**
 * An identifier in the address that is **not** a uuid, as the platform types a few of them.
 *
 * Two of the second tranche's parameters are like this and both are COM-02's: its `categoryId` query
 * parameter is typed `string`, and COM-01's `catalog.categories` publishes its members as an opaque
 * object, so nothing says a category identifier is a uuid. `identifierIn` would drop one as
 * malformed and the screen would draw the wrong category's heading with no clue why.
 *
 * Bounded rather than trusted: a value longer than 200 characters or carrying a control character is
 * treated as absent, because it is going into a query parameter on a request this application makes
 * and a URL is not a place to pass something unexamined.
 */
export function textIn(url: string, parameter: string): string | undefined {
  const value = new URL(url).searchParams.get(parameter);
  if (value === null || value.length === 0 || value.length > 200) return undefined;
  // A control character in a query parameter is not something to pass on unexamined.
  return /\p{Cc}/u.test(value) ? undefined : value;
}

/**
 * A `latitude,longitude` pair in the address, as the two screens before a quote carry a route.
 *
 * `1 Where to` chooses two points and `2 Choose` prices between them, and nothing on this platform
 * holds that pair between the two screens: MOB-02 mints a quote from a pair and publishes it back,
 * but there is no resource for "the route a rider is looking at" before a quote exists. So it lives
 * in the address, which also makes the two screens shareable and makes the acceptance check able to
 * put `2 Choose` into the design's own Westlands-to-JKIA state.
 */
export interface Place {
  readonly latitude: number;
  readonly longitude: number;
}

export function placeIn(url: string, parameter: string): Place | undefined {
  const raw = new URL(url).searchParams.get(parameter);
  if (raw === null) return undefined;
  const parts = raw.split(",");
  if (parts.length !== 2) return undefined;
  const latitude = Number(parts[0]);
  const longitude = Number(parts[1]);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return undefined;
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return undefined;
  return { latitude, longitude };
}

/** A place, as the address writes one. Six decimal places is about a tenth of a metre. */
export function placeParameter(place: Place): string {
  return `${place.latitude.toFixed(6)},${place.longitude.toFixed(6)}`;
}
